import { useState, useEffect } from 'react';
import { Button, LoadingSpinner, Select } from '../../../components/ui';
import { intranetApi as api } from '../../../services/intranet-api';

interface AuditEntry {
  log_id: string;
  action: string;
  actor_name: string;
  area_name: string | null;
  target_user_name: string | null;
  details: Record<string, unknown> | null;
  created_at: string;
}

const ACTION_LABELS: Record<string, string> = {
  AREA_CREATED: 'Area creata',
  AREA_UPDATED: 'Area modificata',
  AREA_DELETED: 'Area eliminata',
  MEMBER_ADDED: 'Utente aggiunto',
  MEMBER_REMOVED: 'Utente rimosso',
  MEMBER_ROLE_CHANGED: 'Ruolo modificato',
  FILE_UPLOADED: 'File caricato',
  FILE_DELETED: 'File eliminato',
  FOLDER_CREATED: 'Cartella creata',
  FOLDER_DELETED: 'Cartella eliminata',
};

const ACTION_FILTER_OPTIONS = [
  { value: '', label: 'Tutte le azioni' },
  { value: 'MEMBER_ADDED', label: 'Utente aggiunto' },
  { value: 'MEMBER_REMOVED', label: 'Utente rimosso' },
  { value: 'MEMBER_ROLE_CHANGED', label: 'Ruolo modificato' },
  { value: 'FILE_UPLOADED', label: 'File caricato' },
  { value: 'FILE_DELETED', label: 'File eliminato' },
  { value: 'FOLDER_CREATED', label: 'Cartella creata' },
  { value: 'AREA_CREATED', label: 'Area creata' },
  { value: 'AREA_UPDATED', label: 'Area modificata' },
  { value: 'AREA_DELETED', label: 'Area eliminata' },
];

const ADMIN_ACTIONS = ['AREA_CREATED', 'AREA_UPDATED', 'AREA_DELETED', 'MEMBER_ADDED', 'MEMBER_REMOVED', 'MEMBER_ROLE_CHANGED'];

function getActorRole(action: string): string {
  return ADMIN_ACTIONS.includes(action) ? 'Admin' : 'Editor';
}

function formatDetails(action: string, details: Record<string, unknown> | null): string {
  if (!details) return '';

  switch (action) {
    case 'MEMBER_ADDED':
      return `Ruolo: ${details.role}`;
    case 'MEMBER_ROLE_CHANGED':
      return `${details.oldRole} → ${details.newRole}`;
    case 'MEMBER_REMOVED':
      return details.previousRole ? `Era: ${details.previousRole}` : '';
    case 'FILE_UPLOADED':
    case 'FILE_DELETED':
      return (details.fileName as string) || '';
    case 'FOLDER_CREATED':
      return (details.fullPath as string) || '';
    default:
      return '';
  }
}

/**
 * Admin — Audit Log Intranet.
 * Scheda consultabile con filtro per azione.
 */
export function IntranetAuditPage() {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const loadAudit = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('pageSize', '30');
      if (actionFilter) params.set('action', actionFilter);

      const response = await api.get<{ data: AuditEntry[]; pagination: { totalPages: number } }>(
        `/admin/audit?${params.toString()}`,
      );
      setEntries(response.data.data);
      setTotalPages(response.data.pagination.totalPages);
    } catch {
      setEntries([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAudit(); }, [page, actionFilter]);

  return (
    <div className="intranet-page">
      <div className="page-header">
        <h2>Audit Log — Intranet</h2>
      </div>

      <div className="filters-bar">
        <Select
          label="Filtra per azione"
          options={ACTION_FILTER_OPTIONS}
          value={actionFilter}
          onChange={(e) => { setActionFilter(e.target.value); setPage(1); }}
        />
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : entries.length === 0 ? (
        <div className="empty-state">
          <p>Nessuna attività registrata.</p>
        </div>
      ) : (
        <>
          <div className="audit-table-wrapper">
          <table className="audit-table">
            <thead>
              <tr>
                <th>Data</th>
                <th>Azione</th>
                <th>Eseguita da</th>
                <th>Area</th>
                <th>Utente target</th>
                <th>Dettagli</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr key={entry.log_id}>
                  <td className="audit-date">
                    {new Date(entry.created_at).toLocaleString('it-IT', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </td>
                  <td>
                    <span className={`audit-action audit-action--${entry.action.toLowerCase()}`}>
                      {ACTION_LABELS[entry.action] || entry.action}
                    </span>
                  </td>
                  <td>{entry.actor_name ? `${entry.actor_name} — ${getActorRole(entry.action)}` : '-'}</td>
                  <td>{entry.area_name || '-'}</td>
                  <td>{entry.target_user_name || '-'}</td>
                  <td className="audit-details">{formatDetails(entry.action, entry.details)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>

          {totalPages > 1 && (
            <div className="pagination" role="navigation" aria-label="Paginazione audit">
              <Button variant="ghost" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
                ← Precedente
              </Button>
              <span className="pagination-info">Pagina {page} di {totalPages}</span>
              <Button variant="ghost" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
                Successiva →
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
